import { RequestTypes, ServiceURL } from '@/types/Enums/ServiceEnums'
import type { IResponse } from '@/types/Utils/IBaseService'
import { Alert, Format } from '@/utils-ts'
import { AxiosManager } from './AxiosManager'

export default class BaseService {
  protected HTTP = AxiosManager.GetAxiosInstance()
  protected URL: string
  protected PATHS: typeof ServiceURL

  private static _isAlertShowing = false

  constructor(url: string) {
    this.URL = url
    this.PATHS = ServiceURL
  }

  private async _triggerErrorAlert(message?: string | null): Promise<void> {
    if (BaseService._isAlertShowing) return
    BaseService._isAlertShowing = true
    try {
      await Alert({ message: message || Format('admin_global_error'), type: 'danger' })
    } finally {
      BaseService._isAlertShowing = false
    }
  }

  private _handleResult<TResponse>(
    result: IResponse<TResponse>,
    showErrorAlert: boolean,
  ): IResponse<TResponse> {
    if (!result.status && showErrorAlert) {
      void this._triggerErrorAlert(result.message)
    }
    return result
  }

  private _handleError<TResponse>(error: any, showErrorAlert: boolean): IResponse<TResponse> {
    const result: IResponse<TResponse> = (error.response?.data as IResponse<TResponse>) ?? {
      status: false,
      data: null,
      total: null,
      message: error.message || 'Unknown error',
    }
    if (showErrorAlert) {
      void this._triggerErrorAlert(result.message)
    }
    return result
  }

  protected async GET<TResponse>(path: string, params: any = null): Promise<IResponse<TResponse>> {
    try {
      const response = await this.HTTP({
        method: RequestTypes.GET,
        url: this.URL + path,
        params,
      })
      return response.data as IResponse<TResponse>
    } catch (error: any) {
      return (
        (error.response?.data as IResponse<TResponse>) ?? {
          status: false,
          data: null,
          total: null,
          message: error.message || 'Unknown error',
        }
      )
    }
  }

  protected async POST<TResponse>(
    path: string,
    data: any = null,
    params: any = null,
    showErrorAlert: boolean = true,
  ): Promise<IResponse<TResponse>> {
    try {
      const response = await this.HTTP({
        method: RequestTypes.POST,
        url: this.URL + path,
        data,
        params,
      })
      return this._handleResult(response.data as IResponse<TResponse>, showErrorAlert)
    } catch (error: any) {
      return this._handleError<TResponse>(error, showErrorAlert)
    }
  }

  protected async DELETE<TResponse>(
    path: string,
    params: any = null,
    data: any = null,
    showErrorAlert: boolean = true,
  ): Promise<IResponse<TResponse>> {
    try {
      const response = await this.HTTP({
        method: RequestTypes.DELETE,
        url: this.URL + path,
        params,
        data,
      })
      return this._handleResult(response.data as IResponse<TResponse>, showErrorAlert)
    } catch (error: any) {
      return this._handleError<TResponse>(error, showErrorAlert)
    }
  }

  protected async PUT<TResponse>(
    path: string,
    data: any = null,
    params: any = null,
    showErrorAlert: boolean = true,
  ): Promise<IResponse<TResponse>> {
    try {
      const response = await this.HTTP({
        method: RequestTypes.PUT,
        url: this.URL + path,
        data,
        params,
      })
      return this._handleResult(response.data as IResponse<TResponse>, showErrorAlert)
    } catch (error: any) {
      return this._handleError<TResponse>(error, showErrorAlert)
    }
  }

  protected async EXCEL(path: string, IsPost: boolean, data: any = null) {
    try {
      const response = await this.HTTP({
        method: IsPost ? RequestTypes.POST : RequestTypes.GET,
        url: this.URL + path,
        data: IsPost ? data : null,
        params: IsPost ? null : data,
        responseType: 'arraybuffer',
      })

      let fileName: string

      if (window.location.origin.includes('localhost')) {
        fileName = 'file.xlsx'
      } else {
        const contentDisposition = response.headers['content-disposition']
        if (!contentDisposition) {
          console.warn('Failed to download file')
        }

        fileName = contentDisposition.split(';')[1].split('=')[1].replaceAll('"', '')
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.warn('Failed to download file', error)
    }
  }
}
